/* global Blob, File, FormData, URL, URLSearchParams, TextEncoder, document, setTimeout */
import { api } from '../../shared/api/client'
import { createZip } from '../../shared/zip/createZip'
import { normalizeLayoutScene, serializeLayoutScene } from './editorLogic'
import {
  normalizeReferenceAnalysis,
  normalizeReferenceCorrections,
  normalizeReferenceRect,
} from './referenceAnalysis'
import type {
  CatalogProgress,
  LayoutEstimate,
  LayoutItem,
  LayoutItemProductionStatus,
  LayoutJob,
  LayoutJobDraft,
  LayoutJobFilters,
  LayoutJobListItem,
  LayoutMutationOptions,
  LayoutJobsListPage,
  LayoutPage,
  LayoutProfile,
  LayoutReference,
  LayoutReferenceAdjustment,
  LayoutReferenceAdjustmentResult,
  LayoutRevision,
  LayoutCatalogCommonFields,
  LayoutCatalogDraft,
  LayoutStarted,
  LayoutSubmissionResult,
  LayoutTemplate,
  LayoutThemeOption,
  LayoutType,
  LayoutValidationIssue,
} from './types'
import { DEFAULT_STUDIO_MODE, isStudioInputMode, isStudioMode } from './studioModes'

const API_PREFIX = '/layout-generator'
const ALL_TYPES: LayoutType[] = ['circle', 'cupcake_5', 'cupcake_6', 'bento', 'topper']
const record = (value: unknown): Record<string, any> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, any>) : {}

export function unwrapLayoutResponse<T>(response: unknown): T {
  const outer = record(response)
  const body = outer.data
  const envelope = record(body)
  return (envelope.data ?? body) as T
}
function mutationConfig(options: LayoutMutationOptions) {
  if (!options?.idempotencyKey?.trim())
    throw new Error('Для изменяющего запроса нужен ключ идемпотентности')
  return { headers: { 'Idempotency-Key': options.idempotencyKey.trim() } }
}
export function createIdempotencyKey(scope: string): string {
  const nonce =
    globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`
  return `layout-generator:${scope}:${nonce}`
}
export function revisionIdempotencyKey(
  scope: string,
  entityId: string,
  revision: number | string,
): string {
  return `layout-generator:${scope}:${encodeURIComponent(entityId)}:r${revision}`
}
export async function submissionIdempotencyKey(jobId: string, revisionIds: string[]): Promise<string> {
  const sorted = [...revisionIds].sort()
  const legacy = `layout-generator:submit:${encodeURIComponent(jobId)}:${sorted.map(encodeURIComponent).join(',')}`
  // Keep accepted short keys stable for retries of existing submissions.
  if (legacy.length <= 160) return legacy
  if (!globalThis.crypto?.subtle) throw new Error('Для безопасной групповой отправки откройте систему через HTTPS или localhost')
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify({ jobId, revisionIds: sorted })))
  return `layout-generator:submit:sha256:${Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')}`
}
export function stablePayloadHash(value: unknown): string {
  const source = JSON.stringify(value)
  let hash = 0x811c9dc5
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(36)
}

export function serializeLayoutDraft(draft: LayoutJobDraft): Record<string, unknown> {
  return {
    name: draft.name.trim(),
    scenario: draft.scenario,
    prompt: draft.prompt.trim() || null,
    theme_number: draft.theme.mode === 'existing' ? draft.theme.themeNumber : null,
    new_theme: draft.theme.mode === 'new' ? { name: draft.theme.newGroupName.trim() } : null,
    theme_attribute: draft.theme.theme.trim() || null,
    catalog_fields: Object.keys(draft.theme.inherited).length ? draft.theme.inherited : null,
    composition: draft.composition
      .filter((row) => row.enabled)
      .map(({ type, count }) => ({ type, count })),
    personalization: {
      texts: draft.texts,
      variation_strength: draft.variationStrength,
      profile_mode: draft.profileMode,
      // Смета и запуск идеи студии читают этот же черновик через getJob и обязаны
      // применить одно и то же правило формирования ТЗ — иначе сервер сверит два
      // разных плана студии и откажет с "Расчёт изменился".
      studio_mode: draft.studioMode,
      // Способ ввода «14» — тем же приёмом, что studio_mode выше: сохраняем в черновике,
      // чтобы LayoutStudioPage видел его сразу из getJob, а не только из sessionStorage
      // моста мастер→студия (тот мост не переживает прямой переход по ссылке).
      input_mode: draft.inputMode ?? null,
    },
    profile_id: draft.profileId,
  }
}

// Предупреждение о разрешении ниже 300 DPI снято 01.10.2026 (для печати на А4 его
// достаточно). Листы, собранные раньше, хранят его в отчёте — не показываем.
// Ошибка того же кода (строгий производственный режим) остаётся видимой.
const isRetiredIssue = (issue: LayoutValidationIssue): boolean =>
  issue.code === 'IMAGE_DPI_LOW' && issue.level === 'warning'

const toIssue = (raw: unknown): LayoutValidationIssue => {
  const value = record(raw)
  return {
    code: String(value.code || 'LAYOUT_VALIDATION_FAILED'),
    message: String(value.message || 'Макет не прошёл проверку'),
    field: value.field == null ? undefined : String(value.field),
    elementId:
      value.elementId == null && value.element_id == null
        ? (Array.isArray(value.elementIds) && value.elementIds.length ? String(value.elementIds[0]) : undefined)
        : String(value.elementId ?? value.element_id),
    ...(Array.isArray(value.elementIds) && value.elementIds.length ? { elementIds: value.elementIds.map(String) } : {}),
    ...(value.details && typeof value.details === 'object' && !Array.isArray(value.details) && Object.keys(value.details).length
      ? { details: value.details as Record<string, unknown> } : {}),
    // Технический валидатор (media-generation-service) называет уровень severity,
    // а не level: читаем оба. Живой прогон 07.09.2026 — 14 готовых листов висели
    // с «Отправка заблокирована до исправления технических ошибок», хотя все
    // замечания были предупреждениями о разрешении печати.
    level:
      value.level === 'warning' || value.severity === 'warning' || value.blocking === false
        ? 'warning'
        : 'error',
  }
}
const revisionApiPath = (id: string, kind: 'pdf' | 'preview') =>
  `${API_PREFIX}/revisions/${encodeURIComponent(id)}/${kind}`
const revisionMediaUrl = (id: string, kind: 'pdf' | 'preview') =>
  `/api/v1${revisionApiPath(id, kind)}`
/** Путь превью ревизии листа — тот же, что у плитки студии; отдаётся гейтвеем с авторизацией. */
export const layoutRevisionPreviewUrl = (id: string): string => revisionMediaUrl(id, 'preview')
const toRevision = (
  raw: unknown,
  fallbackId = '',
  fallbackTemplateId = '',
): LayoutRevision | null => {
  const value = record(raw)
  const id = String(value.id ?? fallbackId)
  if (!id) return null
  const report = record(value.validationReport ?? value.validation_report)
  const pdfReady = Boolean(
    (value.pdfStoragePath ?? value.pdf_storage_path) && (value.pdfSha256 ?? value.pdf_sha256),
  )
  const previewReady = Boolean(
    (value.previewStoragePath ?? value.preview_storage_path) &&
    (value.previewSha256 ?? value.preview_sha256),
  )
  const issues = (
    Array.isArray(report.issues)
      ? report.issues
      : Array.isArray(value.validation)
        ? value.validation
        : []
  ).map(toIssue).filter((issue) => !isRetiredIssue(issue))
  return {
    id,
    number: Number(value.number ?? value.revisionNumber ?? value.revision_number) || 1,
    parentRevisionId:
      value.parentRevisionId == null && value.parent_revision_id == null
        ? null
        : String(value.parentRevisionId ?? value.parent_revision_id),
    createdAt: String(value.createdAt ?? value.created_at ?? ''),
    reason: value.reason == null ? null : String(value.reason),
    scene: normalizeLayoutScene(
      value.scene,
      String(value.templateVersionId ?? value.template_version_id ?? fallbackTemplateId),
    ),
    pdfUrl: pdfReady ? revisionMediaUrl(id, 'pdf') : null,
    previewUrl: previewReady ? revisionMediaUrl(id, 'preview') : null,
    validation: issues,
    validationPassed: report.passed === true,
  }
}
const toReference = (raw: unknown): LayoutReference => {
  const value = record(raw)
  const role = String(value.role)
  return {
    id: String(value.id),
    name: String(value.name ?? value.originalName ?? value.original_name ?? 'Референс'),
    mimeType: String(value.mimeType ?? value.mime_type ?? ''),
    sizeBytes: Number(value.sizeBytes ?? value.size_bytes) || 0,
    pageCount:
      value.pageCount == null && value.page_count == null
        ? null
        : Number(value.pageCount ?? value.page_count),
    selectedPages: Array.isArray(value.selectedPages ?? value.selected_pages)
      ? (value.selectedPages ?? value.selected_pages)
      : undefined,
    role: ['primary', 'style', 'character', 'palette'].includes(role)
      ? (role as LayoutReference['role'])
      : 'style',
    previewUrl:
      value.previewUrl == null && value.preview_url == null
        ? null
        : String(value.previewUrl ?? value.preview_url),
    crop: normalizeReferenceRect(value.crop),
    analysis: normalizeReferenceAnalysis(value.analysis),
    corrections: normalizeReferenceCorrections(value.corrections),
  }
}
const toDraft = (raw: unknown): LayoutJobDraft => {
  const value = record(raw)
  const personalization = record(value.personalization)
  const composition = Array.isArray(value.composition) ? value.composition : []
  const newTheme = record(value.newTheme ?? value.new_theme)
  const themeNumber = value.themeNumber ?? value.theme_number
  return {
    name: String(value.name ?? ''),
    scenario:
      value.scenario === 'reference' || value.scenario === 'close_variant'
        ? value.scenario
        : 'description',
    prompt: String(value.prompt ?? ''),
    references: (Array.isArray(value.references) ? value.references : []).map(toReference),
    theme: {
      mode: themeNumber == null ? 'new' : 'existing',
      themeNumber: themeNumber == null ? null : Number(themeNumber),
      newGroupName: String(newTheme.name ?? newTheme.title ?? ''),
      theme: String(value.themeAttribute ?? value.theme_attribute ?? ''),
      inherited: record(value.catalogFields ?? value.catalog_fields),
    },
    composition: ALL_TYPES.map((type) => {
      const row = composition.find((entry: any) => entry?.type === type)
      return { type, enabled: Boolean(row), count: Number(row?.count) || 1 }
    }),
    texts: Array.isArray(personalization.texts) ? personalization.texts : [],
    profileMode: personalization.profile_mode === 'fast' ? 'fast' : 'economy',
    profileId:
      value.profileId == null && value.profile_id == null
        ? null
        : String(value.profileId ?? value.profile_id),
    variationStrength: personalization.variation_strength === 'moderate' ? 'moderate' : 'small',
    studioMode: isStudioMode(personalization.studio_mode)
      ? personalization.studio_mode
      : DEFAULT_STUDIO_MODE,
    inputMode: isStudioInputMode(personalization.input_mode)
      ? personalization.input_mode
      : undefined,
  }
}
const PRODUCTION_STATUSES: LayoutItemProductionStatus[] = ['none', 'queued', 'in_progress', 'done', 'error']
/** Нет поля или неизвестное значение (старый сервер) — 'none': интерфейс не ломается. */
const toProductionStatus = (raw: unknown): LayoutItemProductionStatus =>
  PRODUCTION_STATUSES.includes(raw as LayoutItemProductionStatus) ? (raw as LayoutItemProductionStatus) : 'none'
const toItem = (raw: unknown): LayoutItem => {
  const item = record(raw)
  const templateId = String(item.templateVersionId ?? item.template_version_id ?? '')
  const revisionId = String(item.currentRevisionId ?? item.current_revision_id ?? '')
  const currentRevision = toRevision(
    item.currentRevision ?? item.current_revision ?? item.revision,
    revisionId,
    templateId,
  )
  const previousRevision = toRevision(
    item.previousRevision ?? item.previous_revision,
    '',
    templateId,
  )
  const history = (Array.isArray(item.revisions) ? item.revisions : [])
    .map((entry: unknown) => toRevision(entry, '', templateId))
    .filter((entry: LayoutRevision | null): entry is LayoutRevision => Boolean(entry))
  const revisions = [...history, currentRevision, previousRevision]
    .filter((entry): entry is LayoutRevision => Boolean(entry))
    .filter((entry, index, rows) => rows.findIndex((row) => row.id === entry.id) === index)
    .sort((left, right) => right.number - left.number)
  return {
    id: String(item.id),
    type: item.type,
    ordinal: Number(item.ordinal) || 1,
    templateVersionId: templateId,
    generationStatus: item.generationStatus ?? item.generation_status ?? 'planned',
    catalogStatus: item.catalogStatus ?? item.catalog_status ?? 'not_submitted',
    currentRevision,
    previousRevision,
    revisions,
    designCode:
      item.designCode == null && item.design_code == null
        ? null
        : String(item.designCode ?? item.design_code),
    error: item.error
      ? {
          code: String(record(item.error).code || 'LAYOUT_ITEM_FAILED'),
          message: String(record(item.error).message || 'Не удалось подготовить макет'),
          retryable: Boolean(record(item.error).retryable),
        }
      : null,
    returnComment:
      item.returnComment == null && item.return_comment == null
        ? null
        : String(item.returnComment ?? item.return_comment),
    canRetryBuild: item.canRetryBuild === true || item.can_retry_build === true,
    productionStatus: toProductionStatus(item.productionStatus ?? item.production_status),
    productionError: textOrNull(item.productionError ?? item.production_error),
  }
}
const textOrNull = (raw: unknown): string | null => {
  const text = typeof raw === 'string' ? raw.trim() : ''
  return text || null
}
const toErrorSummary = (raw: unknown): { code: string; message: string } | null => {
  const value = record(raw)
  const message = typeof value.message === 'string' ? value.message.trim() : ''
  return message ? { code: String(value.code ?? ''), message } : null
}
const toListItem = (raw: unknown): LayoutJobListItem => {
  const value = record(raw)
  const composition = Array.isArray(value.composition) ? value.composition : []
  const items = (Array.isArray(value.items) ? value.items : []).map(toItem)
  const total = composition.reduce((sum: number, row: any) => sum + (Number(row.count) || 0), 0)
  const ready = items.filter((item) => item.generationStatus === 'ready').length
  const firstRevision = items.find((item) => item.currentRevision)?.currentRevision
  const personalization = record(value.personalization)
  const catalogFields = record(value.catalogFields ?? value.catalog_fields)
  return {
    id: String(value.id ?? value.jobId ?? value.job_id),
    method: value.method === 'studio' || value.method === 'duplication' ? value.method : 'direct',
    // Список различает «свободную» студию и «14»; поле хранится внутри
    // personalization_json (см. serializeLayoutDraft) и на списочной выдаче
    // design-service отдаёт его тем же способом, что и на карточке задания.
    studioMode: isStudioMode(value.studioMode ?? value.studio_mode)
      ? (value.studioMode ?? value.studio_mode)
      : isStudioMode(personalization.studio_mode) ? personalization.studio_mode : undefined,
    name: String(value.name || 'Задание без названия'),
    createdByName:
      value.createdByName == null && value.created_by_name == null
        ? null
        : String(value.createdByName ?? value.created_by_name),
    themeNumber:
      value.themeNumber == null && value.theme_number == null
        ? null
        : Number(value.themeNumber ?? value.theme_number),
    themeName:
      value.themeName == null && value.theme_name == null
        ? null
        : String(value.themeName ?? value.theme_name),
    status: value.status || 'draft',
    types: composition.map((row: any) => ({ type: row.type, count: Number(row.count) || 0 })),
    studioState: value.studioState ?? value.studio_state ?? null,
    studioInputMode: isStudioInputMode(value.studioInputMode ?? value.studio_input_mode)
      ? (value.studioInputMode ?? value.studio_input_mode)
      : undefined,
    boardStage: value.boardStage ?? value.board_stage,
    acceptedCount: Number(value.acceptedCount ?? value.accepted_count) || 0,
    // Нет поля (старый сервер) — считаем по листам, если они пришли; иначе 0.
    productionErrorCount:
      Number(value.productionErrorCount ?? value.production_error_count) ||
      items.filter((item) => item.productionStatus === 'error').length,
    sendableCount: Number(value.sendableCount ?? value.sendable_count) || 0,
    hasError: Boolean(value.hasError ?? value.has_error),
    errorSummary: toErrorSummary(value.errorSummary ?? value.error_summary),
    returnedCount: Number(value.returnedCount ?? value.returned_count) || 0,
    themeAttribute: value.themeAttribute ?? value.theme_attribute ?? null,
    catalogTheme: textOrNull(catalogFields.theme),
    brandName: textOrNull(catalogFields.brand_ru ?? catalogFields.brandRu),
    // Список отдаёт can_delete по тем же правилам, что и удаление; нет поля — бэкенд старый, решает сервер при DELETE.
    canDelete:
      value.canDelete == null && value.can_delete == null
        ? undefined
        : Boolean(value.canDelete ?? value.can_delete),
    activeOperationCount: Number(value.activeOperationCount ?? value.active_operation_count) || 0,
    attentionCount: Number(value.attentionCount ?? value.attention_count) || 0,
    queueReviewRequired: Boolean(value.queueReviewRequired ?? value.queue_review_required),
    readyCount: Number(value.readyCount ?? value.ready_count) || ready,
    totalCount: Number(value.totalCount ?? value.total_count) || total || items.length,
    cost: Number(value.actualUsd ?? value.actual_usd ?? value.cost) || 0,
    currency: String(value.currency || 'USD'),
    previewUrl: firstRevision?.previewUrl ?? (value.previewReady === true && value.previewRevisionId ? revisionMediaUrl(String(value.previewRevisionId), 'preview') : null),
    createdAt: String(value.createdAt ?? value.created_at ?? ''),
    updatedAt: String(value.updatedAt ?? value.updated_at ?? ''),
    submittedCount:
      Number(value.submittedCount ?? value.submitted_count) ||
      items.filter((item) => item.catalogStatus !== 'not_submitted').length,
  }
}
const toEstimate = (raw: unknown): LayoutEstimate => {
  const value = record(raw)
  const job = record(value.job)
  const estimate = record(value.estimate ?? value)
  return {
    currency: String(job.currency ?? estimate.currency ?? 'USD'),
    minimum: Number(estimate.estimatedMinUsd ?? estimate.estimated_min_usd) || 0,
    maximum: Number(estimate.estimatedMaxUsd ?? estimate.estimated_max_usd) || 0,
    analysisCost: Number(estimate.analysisCostUsd ?? estimate.analysis_cost_usd) || 0,
    estimatedWait: estimate.estimatedWait == null ? null : String(estimate.estimatedWait),
    warnings: Array.isArray(estimate.warnings) ? estimate.warnings.map(String) : [],
    revision: Number(job.revision ?? estimate.revision) || 1,
  }
}
const toJob = (raw: unknown): LayoutJob => {
  const value = record(raw)
  const items = (Array.isArray(value.items) ? value.items : []).map(toItem)
  const estimate =
    value.estimate || value.estimatedMaxUsd != null || value.estimated_max_usd != null
      ? toEstimate({ job: value, estimate: value.estimate ?? value })
      : null
  return {
    ...toListItem({ ...value, items }),
    revision: Number(value.revision) || 1,
    draft: toDraft(value),
    estimate,
    reservedCost: Number(value.reservedUsd ?? value.reserved_usd) || 0,
    confirmedCost:
      value.confirmedUsd == null && value.confirmed_usd == null
        ? null
        : Number(value.confirmedUsd ?? value.confirmed_usd),
    budgetLimit: Number(value.budgetLimitUsd ?? value.budget_limit_usd) || 0,
    sequence: Number(value.sequence) || 0,
    items,
  }
}
function toViewFacets(raw: unknown): LayoutJobsListPage['facets'] {
  const view = record(record(raw).view)
  const result: NonNullable<LayoutJobsListPage['facets']> = {}
  for (const key of ['all', 'mine_waiting', 'in_work', 'errors', 'ready', 'submitted', 'completed', 'drafts'] as const) {
    const value = Number(view[key])
    if (view[key] != null && Number.isFinite(value)) result[key] = value
  }
  return Object.keys(result).length ? result : undefined
}
function queryFor(filters: LayoutJobFilters, limit: number): string {
  const params = new URLSearchParams()
  const page = Math.max(1, filters.page || 1)
  if (filters.search?.trim()) params.set('search', filters.search.trim())
  for (const status of filters.statuses || []) params.append('status', status)
  for (const stage of filters.stages || []) params.append('stage', stage)
  for (const type of filters.types || []) params.append('type', type)
  if (filters.needsDecision != null) params.set('needsDecision', String(filters.needsDecision))
  if (filters.hasError != null) params.set('hasError', String(filters.hasError))
  if (filters.themeAttribute?.trim()) params.set('themeAttribute', filters.themeAttribute.trim())
  if (filters.group?.trim()) params.set('group', filters.group.trim())
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom)
  if (filters.dateTo) params.set('dateTo', filters.dateTo)
  if (filters.sort) params.set('sort', filters.sort)
  if (filters.view) params.set('view', filters.view)
  for (const method of filters.methods || []) params.append('method', method)
  for (const mode of filters.studioModes || []) params.append('studioMode', mode)
  for (const mode of filters.inputModes || []) params.append('inputMode', mode)
  params.set('offset', String(filters.offset != null ? Math.max(0, Math.floor(filters.offset)) : (page - 1) * limit))
  params.set('limit', String(Math.min(100, limit)))
  return params.toString()
}
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

export const layoutGeneratorAPI = {
  async listTemplates(): Promise<LayoutTemplate[]> {
    const data = unwrapLayoutResponse<any>(await api.get(`${API_PREFIX}/templates`))
    const rows = Array.isArray(data) ? data : data?.templates || []
    return rows.map((entry: any) => ({
      id: String(entry.id),
      type: entry.type,
      title: String(entry.name ?? entry.title ?? entry.code),
      version: Number(entry.version) || 1,
      code: entry.code,
      status: entry.status,
      geometry: record(entry.geometry),
      previewUrl: entry.previewUrl ?? entry.preview_url ?? null,
      description: entry.description ?? null,
    }))
  },
  async listProfiles(): Promise<LayoutProfile[]> {
    const data = unwrapLayoutResponse<any>(await api.get(`${API_PREFIX}/profiles`))
    const rows = Array.isArray(data) ? data : data?.profiles || []
    return rows
      .filter((entry: any) => (entry.executionMode ?? entry.execution_mode) === 'sync')
      .map((entry: any) => ({
        id: String(entry.id),
        title: String(entry.name ?? entry.title ?? entry.code),
        mode: 'fast',
        code: entry.code,
        provider: entry.provider,
        model: entry.model,
        region: entry.region ?? null,
        executionMode: 'sync',
        description: entry.description ?? null,
        estimatedWait: entry.estimatedWait ?? entry.estimated_wait ?? null,
      }))
  },
  async listThemeOptions(search = ''): Promise<LayoutThemeOption[]> {
    const data = unwrapLayoutResponse<any>(
      await api.get('/themes/board', { params: search ? { search } : undefined }),
    )
    const rows = Array.isArray(data?.themes)
      ? data.themes
      : Array.isArray(data?.items)
        ? data.items
        : []
    return rows
      .map((entry: any) => ({
        themeNumber: Number(entry.theme_number ?? entry.themeNumber),
        name: String(entry.name || `Группа № ${entry.theme_number ?? entry.themeNumber}`),
        theme: entry.theme == null ? null : String(entry.theme),
        inherited: record(entry.inherited),
      }))
      .filter(
        (entry: LayoutThemeOption) =>
          Number.isSafeInteger(entry.themeNumber) && entry.themeNumber > 0,
      )
  },
  async uploadReference(
    jobId: string,
    file: File,
    role: LayoutReference['role'],
    options: LayoutMutationOptions,
  ): Promise<LayoutReference> {
    const body = new FormData()
    body.append('file', file)
    body.append('job_id', jobId)
    body.append('role', role)
    return toReference(
      unwrapLayoutResponse(
        await api.post(`${API_PREFIX}/references`, body, {
          ...mutationConfig(options),
          headers: { ...mutationConfig(options).headers, 'Content-Type': undefined },
        }),
      ),
    )
  },
  async updateReferenceRole(
    referenceId: string,
    role: LayoutReference['role'],
    options: LayoutMutationOptions,
  ): Promise<LayoutReference> {
    return toReference(
      unwrapLayoutResponse(
        await api.patch(
          `${API_PREFIX}/references/${referenceId}`,
          { role },
          mutationConfig(options),
        ),
      ),
    )
  },
  async updateReferenceAnalysis(
    referenceId: string,
    expectedRevision: number,
    adjustment: LayoutReferenceAdjustment,
    options: LayoutMutationOptions,
  ): Promise<LayoutReferenceAdjustmentResult> {
    const body = {
      expected_revision: expectedRevision,
      ...(Object.prototype.hasOwnProperty.call(adjustment, 'crop')
        ? { crop: adjustment.crop }
        : {}),
      corrections: {
        designRegion: adjustment.corrections.designRegion,
        textActions: adjustment.corrections.textActions.map((row) => ({
          id: row.id,
          action: row.action,
          ...(row.action === 'replace' ? { replacement: row.replacement?.trim() } : {}),
        })),
      },
    }
    const raw = unwrapLayoutResponse<any>(
      await api.patch(
        `${API_PREFIX}/references/${encodeURIComponent(referenceId)}`,
        body,
        mutationConfig(options),
      ),
    )
    const value = record(raw)
    const reference = toReference(value.reference ?? raw)
    const returnedRevision = Number(
      value.jobRevision ?? value.job_revision ?? record(value.job).revision,
    )
    return {
      reference,
      jobRevision:
        Number.isSafeInteger(returnedRevision) && returnedRevision > expectedRevision
          ? returnedRevision
          : expectedRevision + 1,
    }
  },
  async removeReference(referenceId: string, options: LayoutMutationOptions): Promise<void> {
    await api.delete(
      `${API_PREFIX}/references/${encodeURIComponent(referenceId)}`,
      mutationConfig(options),
    )
  },
  // budgetLimitUsd необязателен: обычный мастер задания задаёт лимит позже через startJob
  // (после estimate). Экран студии, форкающий выбранное направление идеи в отдельное задание
  // (StudioV3Workspace, multi-select на этапе «Идея»), лимит знает сразу и не проходит через
  // estimate/start — design-service принимает budget_limit_usd прямо в теле создания задания
  // (layout-generator/validation.ts: body.budget_limit_usd, default 50, если не передан).
  async createJob(
    draft: LayoutJobDraft,
    options: LayoutMutationOptions,
    budgetLimitUsd?: number,
  ): Promise<LayoutJob> {
    const payload = serializeLayoutDraft(draft)
    if (budgetLimitUsd != null) payload.budget_limit_usd = budgetLimitUsd
    return toJob(
      unwrapLayoutResponse(await api.post(`${API_PREFIX}/jobs`, payload, mutationConfig(options))),
    )
  },
  async patchJob(
    jobId: string,
    revision: number,
    draft: LayoutJobDraft,
    options: LayoutMutationOptions,
  ): Promise<LayoutJob> {
    return toJob(
      unwrapLayoutResponse(
        await api.patch(
          `${API_PREFIX}/jobs/${jobId}`,
          { ...serializeLayoutDraft(draft), expected_revision: revision },
          mutationConfig(options),
        ),
      ),
    )
  },
  // Своё название задания в любом статусе (21.10.2026): ревизию не двигает, поэтому expected_revision не нужен.
  async renameJob(jobId: string, name: string, options: LayoutMutationOptions): Promise<LayoutJob> {
    return toJob(
      unwrapLayoutResponse(
        await api.post(`${API_PREFIX}/jobs/${encodeURIComponent(jobId)}/rename`, { name }, mutationConfig(options)),
      ),
    )
  },
  async estimateJob(
    jobId: string,
    revision: number,
    options: LayoutMutationOptions,
  ): Promise<LayoutEstimate> {
    return toEstimate(
      unwrapLayoutResponse(
        await api.post(
          `${API_PREFIX}/jobs/${jobId}/estimate`,
          { expected_revision: revision },
          mutationConfig(options),
        ),
      ),
    )
  },
  async startJob(
    jobId: string,
    revision: number,
    budgetLimitUsd: number,
    options: LayoutMutationOptions,
  ): Promise<LayoutStarted> {
    const raw = record(
      unwrapLayoutResponse(
        await api.post(
          `${API_PREFIX}/jobs/${jobId}/start`,
          { expected_revision: revision, budget_limit_usd: budgetLimitUsd },
          mutationConfig(options),
        ),
      ),
    )
    return {
      jobId: String(raw.jobId ?? raw.job_id ?? raw.id ?? jobId),
      revision: Number(raw.revision) || revision,
      operationId: raw.operationId ?? raw.operation_id,
    }
  },
  async listJobs(filters: LayoutJobFilters = {}): Promise<LayoutJobsListPage> {
    const limit = filters.limit || 20
    const data = unwrapLayoutResponse<any>(
      await api.get(`${API_PREFIX}/jobs?${queryFor(filters, limit)}`),
    )
    const envelope = record(data)
    const source = Array.isArray(data)
      ? data
      : Array.isArray(envelope.rows)
        ? envelope.rows
        : Array.isArray(envelope.jobs)
          ? envelope.jobs
          : []
    const rows = source.map(toListItem)
    const page = Number(envelope.page ?? filters.page) || 1
    const offset = filters.offset != null ? Math.max(0, Math.floor(filters.offset)) : (page - 1) * limit
    return {
      rows,
      total: Number(envelope.total ?? envelope.pagination?.total) || offset + rows.length,
      page,
      limit: Number(envelope.limit) || limit,
      facets: toViewFacets(envelope.facets),
    }
  },
  async getJob(jobId: string): Promise<LayoutJob> {
    return toJob(unwrapLayoutResponse(await api.get(`${API_PREFIX}/jobs/${jobId}`)))
  },
  /** Паспорт серии: макеты, товары, 1С, карточки и сгруппированные ошибки производства (03.10.2026). */
  async getJobCatalogProgress(jobId: string): Promise<CatalogProgress> {
    return unwrapLayoutResponse<CatalogProgress>(await api.get(`${API_PREFIX}/jobs/${encodeURIComponent(jobId)}/catalog-progress`))
  },
  /** Мягкое удаление задания из списка. 409 JOB_HAS_APPROVED_LAYOUTS, если есть утверждённые или отправленные макеты. */
  async deleteJob(jobId: string, options: LayoutMutationOptions): Promise<{ id: string; deleted: true }> {
    const data = record(
      unwrapLayoutResponse(
        await api.delete(`${API_PREFIX}/jobs/${encodeURIComponent(jobId)}`, mutationConfig(options)),
      ),
    )
    return { id: String(data.id ?? jobId), deleted: true }
  },
  async pauseJob(
    jobId: string,
    revision: number,
    options: LayoutMutationOptions,
  ): Promise<LayoutJob> {
    return toJob(
      unwrapLayoutResponse(
        await api.post(
          `${API_PREFIX}/jobs/${jobId}/pause`,
          { expected_revision: revision },
          mutationConfig(options),
        ),
      ),
    )
  },
  async resumeJob(
    jobId: string,
    revision: number,
    options: LayoutMutationOptions,
  ): Promise<LayoutJob> {
    return toJob(
      unwrapLayoutResponse(
        await api.post(
          `${API_PREFIX}/jobs/${jobId}/resume`,
          { expected_revision: revision },
          mutationConfig(options),
        ),
      ),
    )
  },
  async cancelJob(
    jobId: string,
    revision: number,
    options: LayoutMutationOptions,
  ): Promise<LayoutJob> {
    return toJob(
      unwrapLayoutResponse(
        await api.post(
          `${API_PREFIX}/jobs/${jobId}/cancel`,
          { expected_revision: revision },
          mutationConfig(options),
        ),
      ),
    )
  },
  async saveRevision(
    itemId: string,
    baseRevisionId: string,
    scene: NonNullable<LayoutRevision['scene']>,
    options: LayoutMutationOptions,
  ): Promise<LayoutRevision> {
    const revision = toRevision(
      unwrapLayoutResponse(
        await api.post(
          `${API_PREFIX}/items/${itemId}/revisions`,
          {
            base_revision_id: baseRevisionId,
            scene: serializeLayoutScene(scene),
            reason: 'manual_edit',
          },
          mutationConfig(options),
        ),
      ),
    )
    if (!revision) throw new Error('Сервер не вернул созданную версию макета')
    return revision
  },
  async regenerateItem(
    itemId: string,
    baseRevisionId: string,
    prompt: string,
    budgetLimitUsd: number,
    options: LayoutMutationOptions,
    /** Только эти картинки листа; пусто — все картинки (как раньше). */
    elementIds: string[] = [],
  ): Promise<LayoutStarted> {
    const raw = record(
      unwrapLayoutResponse(
        await api.post(
          `${API_PREFIX}/items/${itemId}/regenerate`,
          {
            scope: elementIds.length ? { kind: 'elements', elementIds } : { kind: 'item' },
            base_revision_id: baseRevisionId,
            prompt,
            budget_limit_usd: budgetLimitUsd,
          },
          mutationConfig(options),
        ),
      ),
    )
    return {
      jobId: String(raw.jobId ?? raw.job_id ?? ''),
      revision: Number(raw.revision) || 1,
      operationId: raw.operationId ?? raw.operation_id,
    }
  },
  /** Повтор сборки листа без версии: сервер заново ставит упавшие операции листа. */
  async retryItem(
    itemId: string,
    budgetLimitUsd: number,
    options: LayoutMutationOptions,
  ): Promise<void> {
    unwrapLayoutResponse(
      await api.post(
        `${API_PREFIX}/items/${itemId}/retry-build`,
        { budget_limit_usd: budgetLimitUsd },
        mutationConfig(options),
      ),
    )
  },
  async validateRevision(
    revisionId: string,
    options: LayoutMutationOptions,
  ): Promise<LayoutValidationIssue[]> {
    const result = record(
      unwrapLayoutResponse(
        await api.post(
          `${API_PREFIX}/revisions/${revisionId}/validate`,
          {},
          mutationConfig(options),
        ),
      ),
    )
    const report = record(result.report)
    return (
      Array.isArray(report.issues)
        ? report.issues
        : Array.isArray(result.issues)
          ? result.issues
          : []
    ).map(toIssue).filter((issue) => !isRetiredIssue(issue))
  },
  async planRecheckStudioRevision(
    jobId: string,
    revisionId: string,
    reason: string,
  ): Promise<{ quote: string; quoteExpiresAt?: string; reserveUsd: number; currency: 'USD'; reviewCalls: 1; expectedRevisionId: string }> {
    const result = unwrapLayoutResponse(await api.post(
      `/layout-studio/${encodeURIComponent(jobId)}/revisions/${encodeURIComponent(revisionId)}/recheck-plan`,
      { reason, expectedRevisionId: revisionId },
    ))
    return result as { quote: string; quoteExpiresAt?: string; reserveUsd: number; currency: 'USD'; reviewCalls: 1; expectedRevisionId: string }
  },
  async recheckStudioRevision(
    jobId: string,
    revisionId: string,
    reason: string,
    quote: string,
    options: LayoutMutationOptions,
  ): Promise<void> {
    await api.post(
      `/layout-studio/${encodeURIComponent(jobId)}/revisions/${encodeURIComponent(revisionId)}/recheck`,
      { reason, expectedRevisionId: revisionId, quote },
      mutationConfig(options),
    )
  },
  async overrideStudioRevision(
    jobId: string,
    revisionId: string,
    reason: string,
    options: LayoutMutationOptions,
  ): Promise<void> {
    await api.post(
      `/layout-studio/${encodeURIComponent(jobId)}/revisions/${encodeURIComponent(revisionId)}/override`,
      { reason, expectedRevisionId: revisionId },
      mutationConfig(options),
    )
  },
  async downloadRevision(revisionId: string, filename: string): Promise<void> {
    const response = await api.get<Blob>(revisionApiPath(revisionId, 'pdf'), {
      responseType: 'blob',
    })
    downloadBlob(response.data, filename)
  },
  async openRevision(revisionId: string): Promise<void> {
    const response = await api.get<Blob>(revisionApiPath(revisionId, 'pdf'), {
      responseType: 'blob',
    })
    const url = URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  },
  /** Скачивание выбранных листов (владелец 02.10.2026): несколько PDF — одним ZIP-архивом
   *  «<название задания>.zip», один лист — сразу PDF. */
  async downloadSelected(
    jobId: string,
    revisionIds: string[],
    options: LayoutMutationOptions,
    archiveName?: string,
  ): Promise<void> {
    const manifest = record(
      unwrapLayoutResponse(
        await api.post(
          `${API_PREFIX}/jobs/${jobId}/download`,
          { revision_ids: revisionIds },
          mutationConfig(options),
        ),
      ),
    )
    const files = (Array.isArray(manifest.files) ? manifest.files : []).map((entry) => {
      const file = record(entry)
      return { revisionId: String(file.revisionId ?? file.revision_id), fileName: String(file.fileName ?? file.file_name ?? 'layout.pdf') }
    })
    if (files.length === 1) {
      await this.downloadRevision(files[0].revisionId, files[0].fileName)
      return
    }
    const entries = []
    for (const file of files) {
      const response = await api.get<Blob>(revisionApiPath(file.revisionId, 'pdf'), { responseType: 'blob' })
      entries.push({ name: file.fileName, data: response.data })
    }
    const zipName = `${(archiveName || 'макеты').replace(/[\/:*?"<>|]+/g, '_').trim() || 'макеты'}.zip`
    downloadBlob(await createZip(entries), zipName)
  },
  /**
   * Предзаполнение мастера «Создать макеты» по выбранным листам задания (контракт К2).
   * Только чтение: поля тематики, строки листов и причины, по которым лист нельзя отправить.
   */
  async getCatalogDraft(jobId: string, revisionIds: string[]): Promise<LayoutCatalogDraft> {
    const raw = record(
      unwrapLayoutResponse(
        await api.get(`${API_PREFIX}/jobs/${encodeURIComponent(jobId)}/catalog-draft`, {
          params: { revision_ids: revisionIds.join(',') },
        }),
      ),
    )
    const text = (value: unknown) => (value == null ? '' : String(value))
    const list = (value: unknown) => (Array.isArray(value) ? value.map(String).filter(Boolean) : [])
    const fields = record(raw.commonFields ?? raw.common_fields)
    const commonFields: LayoutCatalogCommonFields = {
      category: text(fields.category),
      brand_ru: text(fields.brand_ru),
      theme: text(fields.theme),
      gender: text(fields.gender),
      age: text(fields.age),
      occasion: text(fields.occasion),
      recipient_statuses: list(fields.recipient_statuses),
      personas: list(fields.personas),
    }
    const job = record(raw.job)
    const themeNumber = job.themeNumber ?? job.theme_number
    return {
      job: {
        id: text(job.id ?? jobId),
        name: text(job.name),
        themeNumber: themeNumber == null ? null : Number(themeNumber),
        themeName: job.themeName == null && job.theme_name == null ? null : text(job.themeName ?? job.theme_name),
      },
      commonFields,
      commonFieldsLocked: Boolean(raw.commonFieldsLocked ?? raw.common_fields_locked),
      rows: (Array.isArray(raw.rows) ? raw.rows : []).map((entry: any) => ({
        revisionId: text(entry.revisionId ?? entry.revision_id),
        itemId: text(entry.itemId ?? entry.item_id),
        layoutType: entry.layoutType ?? entry.layout_type,
        designType: text(entry.designType ?? entry.design_type),
        ordinal: Number(entry.ordinal) || 0,
        fileName: text(entry.fileName ?? entry.file_name),
        sizeBytes: Number(entry.sizeBytes ?? entry.size_bytes) || 0,
        previewUrl: entry.previewUrl ?? entry.preview_url ?? null,
        ageOnImage: text(entry.ageOnImage ?? entry.age_on_image),
        ageOnImageUnit: text(entry.ageOnImageUnit ?? entry.age_on_image_unit),
      })),
      blockers: (Array.isArray(raw.blockers) ? raw.blockers : []).map((entry: any) => ({
        revisionId: text(entry.revisionId ?? entry.revision_id),
        code: text(entry.code),
        message: text(entry.message),
      })),
    }
  },
  async submitSelected(
    jobId: string,
    revisionIds: string[],
    options: LayoutMutationOptions,
  ): Promise<LayoutSubmissionResult[]> {
    const result = unwrapLayoutResponse<any>(
      await api.post(
        `${API_PREFIX}/jobs/${jobId}/submit`,
        { revision_ids: revisionIds },
        mutationConfig(options),
      ),
    )
    const rows = Array.isArray(result) ? result : result?.results || []
    return rows.map((entry: any) => ({
      itemId: String(entry.itemId ?? entry.item_id ?? ''),
      revisionId: String(entry.revisionId ?? entry.revision_id ?? ''),
      designCode: entry.designCode ?? entry.design_code ?? null,
      status: entry.status,
      error:
        entry.status === 'failed'
          ? {
              code: String(entry.code || 'SUBMISSION_FAILED'),
              message: String(entry.message || 'Не удалось отправить макет'),
            }
          : null,
    }))
  },
}
